import { Fragment } from "react";
import { Plus as PlusIcon } from "@tamagui/lucide-icons";
import { Avatar, Body3, Divider, isWeb, ListItem, Spinner, YStack } from "@medbillai/ui";
import type { PayerOption, usePayerSearch } from "./utils/usePayerSearch";

// Fade rows out at the bottom edge of the list region.
const LIST_EDGE_FADE_MASK = "linear-gradient(to bottom, black calc(100% - 20px), transparent 100%)";
const LIST_MARK = {
  size: "small",
  shape: "square"
} as const;
const LIST_LOGO = {
  ...LIST_MARK,
  "$platform-web": {
    backgroundColor: "$surface2"
  }
} as const;
const SHEET_LOGO = {
  size: "medium",
  width: "$si5.5",
  height: "$si5.5",
  shape: "circle",
  backgroundColor: "$surface2"
} as const;
const SHEET_ROW = {
  paddingHorizontal: "$sp0.5",
  paddingVertical: "$sp1"
} as const;

/** The insurers under a search field: onboarding's page list, or a sheet's. */
export function PayerResults({
  search,
  inSheet = false,
  pendingPayerId = null,
  failed = false,
  onPick,
  onNotListed
}: {
  search: ReturnType<typeof usePayerSearch>;
  /** Round logos, no dividers. */
  inSheet?: boolean;
  /** The row whose pick is saving. */
  pendingPayerId?: string | null;
  failed?: boolean;
  onPick: (payer: PayerOption) => void;
  onNotListed: () => void;
}) {
  const {
    results,
    isSearching,
    noResults,
    listError,
    stale,
    loading
  } = search;
  const logo = inSheet ? SHEET_LOGO : LIST_LOGO;
  const row = inSheet ? SHEET_ROW : null;
  if (loading) {
    return <YStack alignItems="center" paddingVertical="$sp4">
        <Spinner />
      </YStack>;
  }
  return (
    // Web scrolls the list internally so the footer stays visible.
    <YStack
    // Keeps the search focused, so the header doesn't slide back in and
    // move the row out from under the click.
    onMouseDown={isWeb ? e => e.preventDefault() : undefined} $platform-web={{
      flexGrow: 1,
      flexShrink: 1,
      flexBasis: "auto",
      minHeight: 0,
      overflowY: "auto",
      maskImage: LIST_EDGE_FADE_MASK,
      paddingBottom: inSheet ? 0 : "$sp3"
    }} data-sentry-element="YStack" data-sentry-component="PayerResults" data-sentry-source-file="PayerResults.tsx">
      {noResults ? <Body3 color="$textSubtle" paddingVertical={inSheet ? "$sp1.5" : "$sp2"} paddingHorizontal={inSheet ? "$sp0.5" : 0}>
          No results — try a different name.
        </Body3> : null}
      {results.map((payer, index) => <Fragment key={payer.id}>
          {index > 0 && !inSheet ? <Divider thin /> : null}
          <ListItem testID={`select-insurance.payer.${payer.primaryPayerId}`} leftElement={<Avatar {...logo} image={payer.logoUrl ?? undefined} initials={payer.displayName.charAt(0).toUpperCase()} />} rightElement={pendingPayerId === payer.primaryPayerId ? <Spinner size="small" color="$iconSubtle" /> : undefined} {...row} title={payer.displayName} titleNumberOfLines={2} onPress={() => {
          if (!stale) {
            onPick(payer);
          }
        }} />
        </Fragment>)}
      {isSearching ? <>
          {results.length > 0 && !inSheet ? <Divider thin /> : null}
          <ListItem testID="select-insurance.not-listed" leftElement={<Avatar {...inSheet ? SHEET_LOGO : LIST_MARK} icon={PlusIcon} />} {...row} title="I don’t see my insurance" onPress={onNotListed} />
        </> : null}
      {listError != null || failed ? <Body3 color="$textNegative" role="alert" aria-live="polite">
          Something went wrong, please try again.
        </Body3> : null}
    </YStack>
  );
}